import * as React from 'react';
import { FC, useState, useCallback } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import LockIcon from '@material-ui/icons/Lock';
import { useTranslate, useNotify, useRefresh } from 'react-admin';
import { Record } from 'ra-core';

interface CloseQuizBtnProps {
    record?: Record;
    label?: string;
}

const API_URL = process.env.REACT_APP_ADMIN_REST_API_URL;

const CloseQuizBtn: FC<CloseQuizBtnProps> = ({ record }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const isClosed = !!(record && record.is_closed);

    const handleOpen = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        setOpen(true);
    }, []);

    const handleCancel = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        setOpen(false);
    }, []);

    const handleConfirm = useCallback(
        async (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!record) return;
            setLoading(true);
            try {
                const response = await fetch(
                    `${API_URL}/quizzes/${record.id}/close`,
                    { method: 'POST', credentials: 'include' }
                );
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                notify('resources.quizzes.messages.close_success', 'info');
                setOpen(false);
                refresh();
            } catch (e) {
                notify('resources.quizzes.messages.close_error', 'warning');
            } finally {
                setLoading(false);
            }
        },
        [record, notify, refresh]
    );

    if (!record) return null;

    return (
        <>
            <Button
                size="small"
                color="secondary"
                startIcon={<LockIcon />}
                onClick={handleOpen}
                disabled={isClosed}
            >
                {translate('resources.quizzes.actions.close')}
            </Button>
            <Dialog
                open={open}
                onClose={handleCancel}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                aria-labelledby="close-quiz-dialog-title"
            >
                <DialogTitle id="close-quiz-dialog-title">
                    {translate('resources.quizzes.actions.close')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translate('resources.quizzes.messages.close_confirm')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} disabled={loading}>
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        color="primary"
                        autoFocus
                        disabled={loading}
                        startIcon={<LockIcon />}
                    >
                        {translate('resources.quizzes.actions.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CloseQuizBtn;
